import { RefreshCw, Send, X, Zap } from "lucide-react";
import {
  encodeGreenhouseTarget,
  firmwareFitsTarget,
  parseDeployTarget,
} from "services/firmwareTargets";

const formatFileSize = (bytes) => {
  if (!bytes) return "N/A";
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

/**
 * Modal for deploying a firmware version to a device.
 *
 * Props:
 *   isOpen                  - Boolean controlling visibility
 *   onClose                 - Callback to close the modal
 *   devices                 - Array of active device objects
 *   greenhouseControllers   - Optional array of greenhouse controllers; selectable as
 *                             "greenhouse:<mac>" targets (see services/firmwareTargets)
 *   firmwareVersions        - Array of firmware version objects
 *   selectedDevice          - mac_address of a device, or "greenhouse:<mac>" for a controller
 *   onDeviceChange          - (mac_address) => void
 *   selectedFirmwareId      - ID of selected firmware version
 *   onFirmwareChange        - (id) => void
 *   onDeploy                - async () => void
 *   deploying               - Boolean; shows spinner when true
 */
const FirmwareDeployModal = ({
  isOpen,
  onClose,
  devices = [],
  greenhouseControllers = [],
  firmwareVersions = [],
  selectedDevice,
  onDeviceChange,
  selectedFirmwareId,
  onFirmwareChange,
  onDeploy,
  deploying,
}) => {
  if (!isOpen) return null;

  const selectedFirmware = firmwareVersions.find(
    (fw) => fw.id.toString() === selectedFirmwareId
  );
  const target = parseDeployTarget(selectedDevice);
  const selectedController =
    target.kind === "greenhouse"
      ? greenhouseControllers.find((c) => c.mac_address === target.mac)
      : null;
  // Mirrors the backend guards: a controller takes only greenhouse images, a
  // device never gets one. With nothing selected yet, show everything.
  const visibleFirmware = firmwareVersions.filter(
    (fw) => fw.is_active && (!selectedDevice || firmwareFitsTarget(fw, target.kind))
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deploy-modal-title"
      >
        <div className="flex items-center justify-between border-b p-6">
          <h3
            id="deploy-modal-title"
            className="text-xl font-semibold text-gray-900 flex items-center gap-2"
          >
            <Zap className="w-6 h-6 text-blue-600" />
            Deploy Firmware
          </h3>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Device
            </label>
            <select
              value={selectedDevice}
              onChange={(e) => {
                onDeviceChange(e.target.value);
                // Keep a firmware picked from its card (Deploy button) unless it
                // doesn't fit the newly chosen target.
                const nextKind = parseDeployTarget(e.target.value).kind;
                if (selectedFirmware && !firmwareFitsTarget(selectedFirmware, nextKind)) {
                  onFirmwareChange("");
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select a device --</option>
              {devices
                .filter((d) => d.is_active)
                .map((device) => (
                  <option key={device.mac_address} value={device.mac_address}>
                    {device.name || device.mac_address}
                  </option>
                ))}
              {greenhouseControllers.length > 0 && (
                <optgroup label="Greenhouse controllers">
                  {greenhouseControllers.map((controller) => (
                    <option
                      key={controller.mac_address}
                      value={encodeGreenhouseTarget(controller.mac_address)}
                    >
                      {controller.name || controller.mac_address}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>

            {target.kind === "greenhouse" && (
              <div
                className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
                data-testid="greenhouse-deploy-note"
              >
                <p>
                  Running version:{" "}
                  <strong>
                    {selectedController?.last_reported_fw
                      ? `v${selectedController.last_reported_fw}`
                      : "unknown"}
                  </strong>
                  {selectedController && (
                    <span>
                      {" "}
                      &middot; {selectedController.is_online ? "online" : "offline"}
                    </span>
                  )}
                </p>
                <p className="mt-1">
                  The update is refused while the pump is running. A stress run in
                  progress resumes after the controller reboots (a short telemetry
                  gap). A new image that cannot reconnect within about 2 minutes is
                  rolled back automatically.
                </p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Firmware Version
            </label>
            <select
              value={selectedFirmwareId}
              onChange={(e) => onFirmwareChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select firmware --</option>
              {visibleFirmware.map((firmware) => (
                  <option key={firmware.id} value={firmware.id}>
                    v{firmware.version}
                    {firmware.is_latest && " (Latest)"}
                    {firmware.is_stable && " (Stable)"} –{" "}
                    {formatFileSize(firmware.file_size)}
                  </option>
                ))}
            </select>

            {selectedFirmware && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-900">v{selectedFirmware.version}</p>
                {selectedFirmware.description && (
                  <p className="text-gray-600 mt-1">{selectedFirmware.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                  <span>Size: {formatFileSize(selectedFirmware.file_size)}</span>
                  <span>
                    Success Rate:{" "}
                    {selectedFirmware.success_rate
                      ? `${selectedFirmware.success_rate.toFixed(1)}%`
                      : "N/A"}
                  </span>
                  <span>Deployments: {selectedFirmware.deployment_count}</span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> The deployment will be sent via MQTT. The device must
              be online and will report progress back. You can monitor the deployment status
              in the Deployments tab.
            </p>
          </div>
        </div>

        <div className="flex gap-3 border-t p-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onDeploy}
            disabled={deploying || !selectedDevice || !selectedFirmwareId}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {deploying ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Deploying...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Deploy Firmware
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirmwareDeployModal;
